/**
 * /admin/sales-users — admin CRUD for sales reps.
 *
 * Add form: name + email + initial password + role.
 * Per-row: rename / change password / change role / delete.
 *
 * Password is write-only (server hashes); UI never sees the hash. Self-delete
 * blocked server-side; UI also hides the delete button for the current user.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Routes, type SalesUserSummary } from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';
import { useCurrentUser } from '../../lib/useCurrentUser';

interface UserList {
  items: SalesUserSummary[];
}

const roleLabel = (r: SalesUserSummary['role']): string => {
  switch (r) {
    case 'admin':
      return '平台管理员';
    case 'lead':
      return '销售主管';
    case 'sales':
      return '销售员';
    default:
      return r;
  }
};

export function SalesUsersPage() {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['salesUsers'],
    queryFn: () => api.get<UserList>(Routes.admin.salesUsers),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(Routes.admin.salesUser(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['salesUsers'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">销售员账号</h1>
          <p className="text-sm text-slate-500 mt-1">
            管理可以登录系统的人（销售员 / 销售主管 / 平台管理员）。密码以 argon2id 哈希形式入库，页面永远不会返回密码原文。
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAddOpen(true)}>
          + 添加用户
        </button>
      </div>

      {addOpen && (
        <AddUserDialog
          onClose={() => setAddOpen(false)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['salesUsers'] });
            setAddOpen(false);
          }}
        />
      )}
      {editId && (
        <EditUserDialog
          user={users.data?.items.find((u) => u.id === editId)!}
          onClose={() => setEditId(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['salesUsers'] });
            setEditId(null);
          }}
        />
      )}

      <div className="card overflow-x-auto">
        {users.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : !users.data?.items.length ? (
          <div className="text-slate-500 text-sm">还没有用户。</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">姓名</th>
                <th className="py-2 pr-4">邮箱</th>
                <th className="py-2 pr-4">角色</th>
                <th className="py-2 pr-4">在线状态</th>
                <th className="py-2 pr-4">最近活跃</th>
                <th className="py-2 pr-4">创建时间</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.data.items.map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="py-3 pr-4">{u.name}</td>
                  <td className="py-3 pr-4 font-mono">{u.email}</td>
                  <td className="py-3 pr-4">
                    <span className="badge-neutral">{roleLabel(u.role)}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <span
                      className={
                        u.onlineStatus === 'online'
                          ? 'badge-ok'
                          : 'badge-neutral'
                      }
                    >
                      {u.onlineStatus === 'online' ? '在线' : '离线'}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {u.lastSeenAt
                      ? new Date(u.lastSeenAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {new Date(u.createdAt).toLocaleString()}
                  </td>
                  <td className="py-3 pr-4 space-x-2">
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => setEditId(u.id)}
                    >
                      编辑
                    </button>
                    <button
                      className="btn-danger text-xs"
                      disabled={
                        u.id === me.data?.id || deleteMut.isPending
                      }
                      title={u.id === me.data?.id ? '不能删除自己' : ''}
                      onClick={() => {
                        if (confirm(`删除用户 ${u.email}?`)) {
                          deleteMut.mutate(u.id);
                        }
                      }}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AddUserDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'sales' | 'lead' | 'admin'>('sales');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post(Routes.admin.salesUsers, { name, email, password, role }),
    onSuccess: () => onDone(),
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card w-[440px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">添加用户</h2>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setErr(null);
            mut.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <label className="label">姓名</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
            />
          </div>
          <div>
            <label className="label">邮箱（用于登录）</label>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="off"
            />
          </div>
          <div>
            <label className="label">初始密码（至少 8 位）</label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="label">角色</label>
            <select
              className="input"
              value={role}
              onChange={(e) =>
                setRole(e.target.value as 'sales' | 'lead' | 'admin')
              }
            >
              <option value="sales">销售员 — 普通销售，跟自己的线索</option>
              <option value="lead">销售主管 — 看团队所有线索</option>
              <option value="admin">平台管理员 — 全权限</option>
            </select>
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {err}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mut.isPending || !name || !email || !password}
            >
              {mut.isPending ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserDialog({
  user,
  onClose,
  onDone,
}: {
  user: SalesUserSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      if (name !== user.name) patch.name = name;
      if (role !== user.role) patch.role = role;
      if (password) patch.password = password;
      return api.put(Routes.admin.salesUser(user.id), patch);
    },
    onSuccess: () => onDone(),
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  const changed =
    name !== user.name || role !== user.role || password.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card w-[440px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">编辑 {user.email}</h2>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setErr(null);
            mut.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <label className="label">姓名</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div>
            <label className="label">角色</label>
            <select
              className="input"
              value={role}
              onChange={(e) =>
                setRole(e.target.value as 'sales' | 'lead' | 'admin')
              }
            >
              <option value="sales">销售员</option>
              <option value="lead">销售主管</option>
              <option value="admin">平台管理员</option>
            </select>
          </div>
          <div>
            <label className="label">
              新密码 <span className="text-slate-400">（留空 = 不改）</span>
            </label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={password ? 8 : undefined}
              autoComplete="new-password"
            />
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {err}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mut.isPending || !changed}
            >
              {mut.isPending ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
